import {
  IconDotsVertical,
  IconLogout,
  IconUserCircle,
  IconSun,
  IconMoon,
  IconDeviceDesktop,
  IconCreditCard,
  IconCheck,
} from "@tabler/icons-react"
import { useTheme } from "@/app/providers/ThemeContext"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/avatar"
import { Badge } from "@/shared/ui/badge"
import { Skeleton } from "@/shared/ui/skeleton"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/ui/sidebar"
import { useAppContext } from "@/app/providers/AppContext"

export function NavUser({
  user,
  onLogout,
  onNavigate,
  loading = false,
  country = "US",
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
  onLogout?: () => void
  onNavigate?: (tab: string) => void
  loading?: boolean
  country?: string
}) {
  const { isMobile } = useSidebar()
  const { logout } = useAppContext()
  const { themeMode, setThemeMode } = useTheme()

  // Generate initials from name
  const generateInitials = (name: string): string => {
    if (!name) return "U"
    const trimmed = name.trim()
    const parts = trimmed.split(/\s+/)
    
    if (parts.length >= 2) {
      // Has first and last name, take first letter of each
      return (parts[0][0] + parts[1][0]).toUpperCase()
    } else {
      // No space, take first two letters
      return trimmed.substring(0, 2).toUpperCase()
    }
  }

  const initials = generateInitials(user.name)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="hover:bg-transparent active:bg-transparent data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground focus-visible:ring-0 focus-visible:outline-none outline-none"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">{loading ? "" : initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                {loading ? (
                  <Skeleton className="h-4 w-32" />
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="truncate font-sans">{user.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {country}
                      </Badge>
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </>
                )}
              </div>
              <IconDotsVertical className="ml-auto size-4 shrink-0" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{loading ? "" : initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  {loading ? (
                    <Skeleton className="h-4 w-32" />
                  ) : (
                    <>
                      <span className="truncate font-sans">{user.name}</span>
                    </>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => onNavigate?.("account")}>
                <IconUserCircle />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onNavigate?.("plans")}>
                <IconCreditCard />
                Billing
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <IconSun className="size-4" />
                  Theme
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setThemeMode("light")}>
                    <IconSun />
                    Light Mode
                    {themeMode === "light" ? <IconCheck className="ml-auto" /> : null}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setThemeMode("dark")}>
                    <IconMoon />
                    Dark Mode
                    {themeMode === "dark" ? <IconCheck className="ml-auto" /> : null}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setThemeMode("system")}>
                    <IconDeviceDesktop />
                    System Mode
                    {themeMode === "system" ? <IconCheck className="ml-auto" /> : null}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                onLogout?.();
                await logout();
              }}
            >
              <IconLogout />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
